import { useQuery } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import type { RootStateOrAny } from 'react-redux';

import type {
  GetClaimSummaryResponse,
  SimplifiedTreatment,
  SrpBoneLevel,
  TImageDisplay,
  ToothWithFindings,
} from '@/modules/insuranceExport/types';
import { makeAPIRequestReactQuery } from '@/react-query/api';
import { endpoints } from '@/react-query/endpoints';

export const useClaimSummary = ({
  estimatedVisitId,
  unifiedAppointmentId,
}: {
  estimatedVisitId?: number;
  unifiedAppointmentId?: number;
}) => {
  const {
    primaryPracticeId: practiceId,
    primaryOrganizationId: organizationId,
  } = useSelector((state: RootStateOrAny) => state.login.user);

  const path = `/v5/${endpoints.claimFindings}`;

  const { data: claimSummary, isLoading: isLoadingClaimSummary } = useQuery(
    [path, estimatedVisitId, unifiedAppointmentId],
    async () => {
      const resp = await makeAPIRequestReactQuery<GetClaimSummaryResponse>(
        path,
        {},
        'POST',
        {
          data: {
            estimatedVisitId: Number(estimatedVisitId),
            unifiedAppointmentId,
          },
          headers: {
            Org: organizationId,
            Practice: practiceId,
          },
        },
      );
      return resp;
    },
    {
      enabled: !!estimatedVisitId,
    },
  );

  // Process imagesDisplay to combine treatments for the same tooth
  const getTeethWithTreatments = (
    imagesDisplay: TImageDisplay[] | undefined | null,
  ) => {
    if (!imagesDisplay || imagesDisplay.length === 0) {
      return [];
    }

    // Create a map to store combined treatments by tooth
    const toothTreatmentsMap = new Map<string, SimplifiedTreatment[]>();

    // Flatten all treatments from all images into a single array
    const allTreatments = imagesDisplay.flatMap((imageDisplay) =>
      imageDisplay.txs.flatMap((treatment) =>
        treatment.teeth.map((tooth) => ({
          txId: treatment.txId,
          tooth,
        })),
      ),
    );

    // Process all treatments in a single loop
    allTreatments.forEach(({ txId, tooth }) => {
      const simplifiedTreatment: SimplifiedTreatment = {
        txId,
        tooth,
      };

      if (toothTreatmentsMap.has(tooth.toothId)) {
        toothTreatmentsMap.get(tooth.toothId)?.push(simplifiedTreatment);
      } else {
        toothTreatmentsMap.set(tooth.toothId, [simplifiedTreatment]);
      }
    });

    // Convert the map to an array of objects with tooth and treatments
    return Array.from(toothTreatmentsMap.entries()).map(
      ([toothId, treatments]) => ({
        toothId,
        treatments,
      }),
    );
  };

  // Transform teethWithTreatments into teethWithFindings
  const getTeethWithFindings = (
    teethWithTreatments: {
      toothId: string;
      treatments: SimplifiedTreatment[];
    }[],
  ): ToothWithFindings[] => {
    if (!teethWithTreatments || teethWithTreatments.length === 0) {
      return [];
    }

    return teethWithTreatments.map(({ toothId, treatments }) => {
      // Initialize findings with default values
      const findings = {
        caries: [] as string[],
        boneLevels: null as SrpBoneLevel[] | null,
        restorativeAdjacentLucency: [] as string[],
        dmf: 0,
      };

      // Combine findings from all treatments for this tooth
      treatments.forEach((treatment) => {
        const tooth = treatment.tooth;

        // For each property, keep the defined value if current value is null/undefined/empty
        if (
          tooth.caries &&
          tooth.caries.length > 0 &&
          (!findings.caries || findings.caries.length === 0)
        ) {
          findings.caries = tooth.caries;
        }

        if (
          tooth.boneLevelsV2 &&
          (!findings.boneLevels ||
            Object.keys(findings.boneLevels).length === 0)
        ) {
          findings.boneLevels = tooth.boneLevelsV2;
        }

        if (
          tooth.restorativeAdjacentLucency &&
          tooth.restorativeAdjacentLucency.length > 0 &&
          (!findings.restorativeAdjacentLucency ||
            findings.restorativeAdjacentLucency.length === 0)
        ) {
          findings.restorativeAdjacentLucency =
            tooth.restorativeAdjacentLucency;
        }

        if (
          tooth.dmf !== undefined &&
          tooth.dmf !== null &&
          findings.dmf === 0
        ) {
          findings.dmf = tooth.dmf;
        }
      });

      return {
        toothId,
        findings,
      };
    });
  };

  const teethWithTreatments = getTeethWithTreatments(
    claimSummary?.imagesDisplay,
  );

  const teethWithFindings = getTeethWithFindings(teethWithTreatments);

  return {
    claimSummary,
    teethWithTreatments,
    teethWithFindings,
    isLoadingClaimSummary,
  };
};
